// @ts-nocheck
import * as React from "react";

// Shared bits used across screens — logo, sidebar nav, topbar, icons.

// 시스템 설정 > 일반의 "플랫폼 이름"(general.site_name). show.html.erb 가 메타 태그로
// 주입한다. 비로그인 랜딩에서도 Logo 가 쓰여 settings API(인증 필요)를 못 쓰므로
// 메타 태그를 동기적으로 읽는다. 미주입 시 기본값으로 폴백.
const siteName = () =>
  (typeof document !== 'undefined' &&
    document.querySelector('meta[name="site-name"]')?.getAttribute('content')) ||
  "통합돌봄 CARE·OS";

// 고객지원번호(general.support_phone). show.html.erb 가 메타 태그로 주입한다.
// 화면 곳곳의 고객지원/상담 전화(footer·로그인 안내·어르신/보호자 상담센터)에서 쓴다.
// 미주입 시 기본값으로 폴백.
const supportPhone = () =>
  (typeof document !== 'undefined' &&
    document.querySelector('meta[name="support-phone"]')?.getAttribute('content')) ||
  "1588-0000";

// 사이트 권역 — 이 배포가 대표하는 단일 권역(남양주 N-Care 2.0).
// design#show 가 <meta name="site-region-*"> 로 주입한다(Region.site_default).
// 랜딩/로그인이 "권역 선택" 없이 이 권역만 다루기 위한 단일 출처.
const siteRegion = () => {
  const meta = (n: string) =>
    (typeof document !== 'undefined' &&
      document.querySelector(`meta[name="${n}"]`)?.getAttribute('content')) || "";
  const id = Number(meta('site-region-id'));
  return { id: Number.isFinite(id) && id > 0 ? id : null, name: meta('site-region-name') || "남양주시" };
};

// 마을교육공동체(돌봄연계국 C) 피처 플래그 — design#show 가 메타 태그로 주입.
// 랜딩 푸터/모바일 드로어가 /village 보조 링크를 조건부 노출한다. off(기본)면
// 링크 자체가 안 뜨므로 외부 발견성이 0 — 기존 통합돌봄 사용자에게 미노출 유지.
const villageEnabled = () =>
  (typeof document !== 'undefined' &&
    document.querySelector('meta[name="village-enabled"]')?.getAttribute('content')) === "true";

// N-Care 2.0 브랜드 자산(public/brand). 랜딩·로그인·모바일 랜딩이 공유한다.
//  hero — 제공받은 표지 이미지의 사진 패널(로고+악수+슬로건)만 잘라낸 것
//  logo — 가로형 워드마크(로고 + "N-Care 2.0")
//  mark — 정사각 심볼(N + 하트)
const BRAND_IMG = {
  // cover — 제공받은 표지 이미지 원본(좌: 카피 / 우: 사진). 랜딩 히어로 배경으로 통째 쓴다.
  cover: "/brand/ncare-cover.png",
  hero: "/brand/ncare-hero.png",
  // photo — 표지의 사진 영역만(글자 없는 구간). 배경 이미지로 깔고 위에 글자를 얹을 때.
  photo: "/brand/ncare-photo.png",
  logo: "/brand/ncare-logo.png",
  mark: "/brand/ncare-mark.png",
};

// N-Care 2.0 표지의 네이비 계열 — 전역 토큰(주황)은 그대로 두고 공개 화면
// (랜딩/로그인)에서만 브랜드 색으로 얹는다.
const NCARE = {
  navy:   "#16324F",
  navy2:  "#20456B",
  navy3:  "#2E5C89",
  ink:    "#0E2438",
  soft:   "#EAF1F7",
  soft2:  "#D7E5F0",
  accent: "#3E7CB1",
};

// "통합돌봄 CARE·OS" → { name: "통합돌봄", sub: "CARE·OS", mark: "통" }.
// 첫 공백 기준으로 주명/부제를 나눈다(공백 없으면 부제 없음). 마크는 첫 글자.
const brandParts = (raw: string) => {
  const s = (raw || "").trim();
  const i = s.indexOf(" ");
  const name = i === -1 ? s : s.slice(0, i);
  const sub  = i === -1 ? "" : s.slice(i + 1).trim();
  return { name, sub, mark: Array.from(name)[0] || "" };
};

const Logo = ({ size = 30 }) => {
  const { name, sub, mark } = brandParts(siteName());
  return (
    <a href="/" className="brand" style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="brand-mark" style={{ width: size, height: size, fontSize: size * 0.45 }}>{mark}</div>
      <div>
        <div className="brand-name">{name}</div>
        {sub && <div className="brand-sub">{sub}</div>}
      </div>
    </a>
  );
};

// Tiny line icons (16×16 viewBox 24).
const Ic = {
  dashboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>,
  region:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0Z"/><circle cx="12" cy="10" r="3"/></svg>,
  org:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V8l9-5 9 5v13"/><path d="M9 21V12h6v9"/></svg>,
  manager:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/></svg>,
  case:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-4.5-7-11a7 7 0 1 1 14 0c0 6.5-7 11-7 11Z"/><path d="M9 10h6M12 7v6"/></svg>,
  schedule:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/></svg>,
  map:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4l-6 2v14l6-2 6 2 6-2V4l-6 2-6-2Z"/><path d="M9 4v14M15 6v14"/></svg>,
  match:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h6m6 0h6"/><path d="M9 8l-4 4 4 4M15 8l4 4-4 4"/></svg>,
  approve:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="9"/></svg>,
  bell:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 16V11a6 6 0 1 1 12 0v5l2 3H4l2-3Z"/><path d="M10 21h4"/></svg>,
  chart:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h17"/><path d="M7 16V9M12 16V5M17 16v-4"/></svg>,
  settings:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1A2 2 0 1 1 4.4 16.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.3.7 1 1.1 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"/></svg>,
  search:    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>,
  upload:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v13"/></svg>,
  copy:      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  plus:      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>,
  filter:    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M3 5h18M6 12h12M10 19h4"/></svg>,
  more:      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>,
  caret:     <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m6 9 6 6 6-6"/></svg>,
  user:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/></svg>,
  heart:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-4.4-9.5-9A5.5 5.5 0 0 1 12 6.5 5.5 5.5 0 0 1 21.5 12c-2.5 4.6-9.5 9-9.5 9Z"/></svg>,
  alert:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 21h20L12 2Z"/><path d="M12 9v6M12 18v.5"/></svg>,
  check:     <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 13 4 4L19 7"/></svg>,
  close:     <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>,
  arrow:     <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>,
  clock:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
  phone:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1 1.2.4 2.4.7 3.6a2 2 0 0 1-.4 2L8 10.6a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2-.5c1.2.4 2.4.6 3.6.7a2 2 0 0 1 1.7 2.1Z"/></svg>,
  chat:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.4 9 9 0 0 1-3.9-.9L3 20l1.4-3.5A8.2 8.2 0 0 1 3 11.5 8.4 8.4 0 0 1 11.5 3 8.4 8.4 0 0 1 21 11.5Z"/></svg>,
  help:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.3 9.3a2.7 2.7 0 0 1 5.2.9c0 1.8-2.7 2.7-2.7 2.7"/><path d="M12 17h.5"/></svg>,
  pill:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="8.5" width="19" height="7" rx="3.5" transform="rotate(-45 12 12)"/><path d="m8.5 8.5 7 7"/></svg>,
};

// 409 응답을 사람 친화 문구로. 서버는 일정 겹침(schedule_conflict) 등에 정확한
// message + conflict 상세를 내려주는데, 화면들이 이를 버리고 "이미 처리된 항목…"
// 으로 뭉뚱그려 오해를 줬다(겹침은 새로고침으로 해소 안 됨). 서버 message를 우선
// 노출하고, 겹치는 방문의 시각/어르신을 덧붙인다. message가 없을 때만 fallback.
// 공통 에러 코드 → 사용자용 한글 문구. 백엔드가 친화 message 없이 {error:"코드"}만
// 줄 때(주로 권한/매칭 도메인) 이 맵으로 변환한다. 새 코드가 생기면 여기 한 줄 추가.
const ERROR_CODE_KO = {
  // 인증/권한
  unauthorized: "로그인이 필요합니다.",
  forbidden: "권한이 없습니다.",
  invalid_credentials: "이메일 또는 비밀번호가 올바르지 않습니다.",
  account_locked: "계정이 잠금 상태입니다. 잠시 후 다시 시도하거나 관리자에게 문의해주세요.",
  account_off: "탈퇴 처리된 계정입니다. 관리자에게 문의해주세요.",
  not_found: "대상을 찾을 수 없습니다.",
  // 매칭(COD)
  already_decided: "이미 처리된 매칭입니다. 새로고침해주세요.",
  not_held: "보류 상태가 아니거나 이미 배정되었습니다. 새로고침해주세요.",
  case_not_matching: "매칭 단계의 케이스가 아닙니다.",
  invalid_decision: "잘못된 결정 값입니다.",
  invalid_weights: "가중치 값이 올바르지 않습니다.",
  no_match: "연결된 매칭이 없습니다.",
  no_candidates: "더 이상 추천할 매니저가 없습니다.",
  // 케이스/방문/신청
  open_case_exists: "이미 진행 중인 케이스가 있습니다.",
  no_changes: "변경된 내용이 없습니다.",
  edit_window_closed: "수정 가능 시간이 지났습니다.",
  reason_required: "사유를 입력해주세요.",
  already_requested: "이미 처리 중인 신청이 있습니다.",
  organization_not_care: "케어매니저는 요양/재가 기관으로만 변경할 수 있습니다.",
  has_care_managers: "소속 케어매니저가 있어 처리할 수 없습니다. 먼저 이관하세요.",
  has_linked_users: "연결된 사용자 계정이 있어 처리할 수 없습니다. 먼저 계정 소속을 정리하세요.",
  // 가입/검증 일반
  invalid: "입력하신 정보를 다시 확인해주세요.",
  invalid_profile: "입력 정보를 다시 확인해주세요.",
  validation_failed: "입력값을 다시 확인해주세요.",
  constraint: "연결된 데이터가 남아 있어 처리할 수 없습니다.",
};

function statusFallbackKO(status) {
  if (status === 401) return "로그인이 필요합니다.";
  if (status === 403) return "권한이 없습니다.";
  if (status === 404) return "대상을 찾을 수 없습니다.";
  if (status === 409) return "이미 처리되었거나 충돌이 발생했습니다. 새로고침해주세요.";
  if (status && status >= 500) return "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
  return "처리 중 오류가 발생했습니다.";
}

// API 에러를 사용자용 한글 메시지로 변환하는 단일 진입점. 기계용 코드(error)나
// 기술 문자열(HTTP 500 등)을 절대 그대로 노출하지 않는다.
// 두 가지 입력을 모두 받는다:
//   1) 파싱된 응답 body 객체 {error, message, details}  → apiErrorMessage(body, res.status)
//   2) throw된 Error(또는 문자열)                        → apiErrorMessage(err)
// 우선순위: details(구체적 검증 메시지) → message(서버 친화 문구) → 코드맵 → 상태별 폴백.
function apiErrorMessage(input, status?) {
  // (2) Error/문자열 경로 — jsonFetch류가 body.message를 Error.message로 던지거나
  //     body.error를 .code로 붙여 던지는 두 관례를 모두 흡수한다.
  if (input instanceof Error || typeof input === "string") {
    const e: any = input;
    // api.ts httpError가 붙인 구조화 필드가 있으면 body 객체 경로로 위임해 문서화된
    // 우선순위(details→message→코드맵→상태)를 그대로 적용한다. 이게 없으면 서버가 준
    // 구체적 사유가 "422 ... {json}" 문자열에 묻혀 일반 폴백으로 빠진다.
    if (e && (e.code || e.serverMessage || (Array.isArray(e.details) && e.details.length))) {
      return apiErrorMessage(
        { error: e.code, message: e.serverMessage, details: e.details },
        e.status ?? status,
      );
    }
    const code = (e && e.code) || null;
    if (code && ERROR_CODE_KO[code]) return ERROR_CODE_KO[code];
    const raw = String(e?.message ?? e ?? "").trim();
    if (!raw) return statusFallbackKO(status);
    // 호출부가 코드 문자열(예: "forbidden")을 그대로 넘긴 경우도 한글로.
    if (ERROR_CODE_KO[raw]) return ERROR_CODE_KO[raw];
    // 네트워크 실패(fetch 자체 throw)는 브라우저별 영문 문구라 일반 한글로.
    if (/failed to fetch|networkerror|load failed|network request failed/i.test(raw)) {
      return "네트워크 연결을 확인해주세요.";
    }
    // "HTTP 500", "404 Not Found {..}", "요청 실패 (409)" 같은 기술 문자열은 상태 폴백으로.
    const m = raw.match(/\b(401|403|404|409|5\d\d)\b/);
    if (/^HTTP\s+\d/i.test(raw) || /^\d{3}\b/.test(raw) || (/(요청 실패|HTTP|오류)/.test(raw) && m)) {
      return statusFallbackKO(m ? Number(m[1]) : status);
    }
    return raw; // 이미 한글 message면 그대로
  }
  // (1) body 객체 경로
  const body: any = input || {};
  if (Array.isArray(body.details) && body.details.length) return body.details.join("\n");
  if (body.message) return body.message;
  if (body.error && ERROR_CODE_KO[body.error]) return ERROR_CODE_KO[body.error];
  return statusFallbackKO(status ?? body.status);
}

function conflictMessage(body, fallback = "이미 처리된 항목입니다. 새로고침해주세요.") {
  if (!body?.message) return fallback;
  const c = body.conflict;
  if (c?.scheduled_at) {
    const t = new Date(c.scheduled_at).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
    return `${body.message} (겹치는 방문: ${t}${c.elderly_name ? ` · ${c.elderly_name} 어르신` : ""})`;
  }
  return body.message;
}

// PasswordInput: <input type="password"> with an eye-icon visibility toggle.
// Wrapper carries the caller-supplied `style` so margins behave like a plain
// <input>; the inner input always reserves right padding for the toggle button.
function PasswordInput({ style, className = "input", error, ...rest }) {
  const [show, setShow] = React.useState(false);
  return (
    <div style={{position:'relative', ...(style || {})}}>
      <input {...rest} className={className}
             type={show ? 'text' : 'password'}
             style={{width:'100%', paddingRight:40,
                     ...(error ? {borderColor:'var(--rose-500)', boxShadow:'0 0 0 2px rgba(244,63,94,0.15)'} : null)}}/>
      <button type="button" onClick={()=>setShow(v=>!v)}
              aria-label={show ? '비밀번호 숨기기' : '비밀번호 표시'}
              aria-pressed={show}
              style={{position:'absolute', right:6, top:'50%', transform:'translateY(-50%)',
                      background:'transparent', border:'none', cursor:'pointer', padding:6,
                      color:'var(--ink-3)', display:'grid', placeItems:'center', borderRadius:6}}>
        {show ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 2l20 20"/>
            <path d="M6.7 6.7C4.6 8.1 3 10.3 2 12c1.8 3.5 6 7 10 7 1.8 0 3.5-.5 5-1.3"/>
            <path d="M9.9 5.1A11.5 11.5 0 0 1 12 5c4 0 8.2 3.5 10 7-.7 1.4-1.8 2.8-3.2 3.9"/>
            <path d="M14.1 14.1A3 3 0 0 1 9.9 9.9"/>
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        )}
      </button>
    </div>
  );
}

// 한국 전화번호 자동 하이픈 포맷. 숫자만 추출 후 입력 양식(02 지역번호 / 휴대폰·
// 일반 지역번호 3자리)에 맞춰 하이픈을 끼운다. 부분 입력 중에도 자연스럽게 동작.
//   01012345678 → 010-1234-5678,  0212345678 → 02-1234-5678,  0312345678 → 031-234-5678
function formatPhoneKR(value) {
  const d = String(value || "").replace(/\D/g, "").slice(0, 11);
  if (d.startsWith("02")) {
    // 서울 지역번호(02): 2-3/4-4
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  }
  // 휴대폰(010 등) 및 3자리 지역번호: 3-3/4-4
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length <= 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

// fmtDuration: 분 단위 숫자를 사람이 읽기 좋은 단위로 환산한다.
//   60분 미만 → "N분", 24시간 미만 → "N시간", 30일 미만 → "N일", 그 이상 → "N달"
// 소수 첫째 자리까지 표기하되 정수면 소수점은 떼고, 1달=30일·1일=24시간 기준.
function fmtDuration(mins) {
  const v = Number(mins);
  if (!isFinite(v) || v < 0) return "·";
  const round1 = (x) => {
    const r = Math.round(x * 10) / 10;
    return Number.isInteger(r) ? String(r) : r.toFixed(1);
  };
  if (v < 60) return `${round1(v)}분`;
  if (v < 60 * 24) return `${round1(v / 60)}시간`;
  if (v < 60 * 24 * 30) return `${round1(v / (60 * 24))}일`;
  return `${round1(v / (60 * 24 * 30))}달`;
}

// PhoneInput: 연락처 전용 입력. 입력 즉시 한국 전화번호 양식으로 하이픈을 자동
// 삽입한다. onChange는 일반 <input>과 동일 시그니처(e.target.value는 포맷된 값).
function PhoneInput({ value, onChange, className = "input", ...rest }) {
  return (
    <input
      {...rest}
      className={className}
      type="tel"
      inputMode="numeric"
      autoComplete="tel"
      maxLength={13}
      placeholder={rest.placeholder ?? "010-0000-0000"}
      value={formatPhoneKR(value)}
      onChange={(e) => {
        const formatted = formatPhoneKR(e.target.value);
        if (onChange) onChange({ ...e, target: { ...e.target, value: formatted } });
      }}
    />
  );
}

// ─── Current user hook (Phase 4) ─────────────────────────
// 사이드바 nav_counts(승인 대기 등)는 다른 화면에서 액션이 일어나면 즉시 갱신
// 되어야 한다. window 이벤트 "session:refresh"를 dispatch하면 모든 useCurrentUser
// 인스턴스가 re-fetch한다. 화면 코드는 액션 성공 후 다음 한 줄을 호출하면 됨:
//   window.dispatchEvent(new Event("session:refresh"));
function useCurrentUser() {
  const [user, setUser] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);
  const fetchMe = React.useCallback(() => {
    fetch("/api/v1/sessions/me", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : null)
      .then(u => { setUser(u); setLoaded(true); });
  }, []);
  React.useEffect(() => {
    fetchMe();
    const handler = () => fetchMe();
    window.addEventListener("session:refresh", handler);
    return () => window.removeEventListener("session:refresh", handler);
  }, [fetchMe]);
  return { user, loaded, refresh: fetchMe };
}

async function logout() {
  await fetch("/api/v1/sessions", { method: "DELETE", credentials: "same-origin" });
  window.location.href = "/login";
}

// Map server User.role → sidebar nav key.
const SIDEBAR_ROLE_FOR_USER = {
  system_admin: 'platform',
  region_admin: 'region',
  coordinator:  'region',  // 코디네이터는 권역 사이드바 재사용(실무 조정 역할)
  agency:       'agency',
  manager:      'manager',
  guardian:     'guardian',
  citizen:      'guardian',
};

// App-shell sidebar.
// `role` prop is a fallback for logged-out (canvas/demo) usage.
// When a user is logged in, the sidebar always reflects the user's role,
// so a region admin never sees the platform sidebar (and vice versa).
//
// Each nav item may declare `requires: <capability_key>` to gate visibility
// on the user's capability flags (from sessions#me → user.capabilities).
// Items without `requires` are visible to anyone seeing the nav group. The
// capability keys match Setting::PERMISSION_KEYS on the Rails side so the
// UI gate and the backend gate share a single source.
function Sidebar({ role: propRole = 'region', active }) {
  const NAVS = {
    // Sidebar items. Counts are intentionally NOT hardcoded here — they
    // come from sessions#me → nav_counts_for so the badge reflects real
    // backend state for the logged-in operator. The merger below applies
    // counts[it.k] onto each item when present.
    region: [
      { g: '운영' },
      { k: 'dashboard', icon: 'dashboard', label: '권역 대시보드',     href: '/dashboard/region', hideRoles: ['agency'] },
      { k: 'cases',     icon: 'case',      label: '케이스 목록',        href: '/cases',         hideRoles: ['agency'] },
      { k: 'elderly',   icon: 'heart',     label: '어르신 명단',        href: '/elderly',       hideRoles: ['agency'] },
      { k: 'cod',       icon: 'match',     label: 'AI 자동매칭(COD)', href: '/cod' },
      // 자원은행 조건검색 — 기관 후보를 찾는 화면(케어매니저 매칭인 COD 와는 별개 흐름).
      { k: 'resources', icon: 'search',    label: '자원 조건검색',      href: '/resources', requires: 'can_decide_matches' },
      { k: 'map',       icon: 'map',       label: '지도',              href: '/map' },
      { k: 'schedule',  icon: 'schedule',  label: '일정 관리',          href: '/schedule' },
      { k: 'approval',  icon: 'approve',   label: '승인 관리',          href: '/approval',      requires: 'can_approve' },
      { g: '관리' },
      { k: 'region',    icon: 'region',    label: '권역 관리',          href: '/regions',       requires: 'can_manage_regions' },
      { k: 'org',       icon: 'org',       label: '유관기관',           href: '/organizations' },
      { k: 'manager',   icon: 'manager',   label: '케어매니저',         href: '/managers' },
      { k: 'members',   icon: 'user',      label: '회원 관리',          href: '/members',       requires: 'can_manage_members' },
      { k: 'report',    icon: 'chart',     label: '리포트',             href: '/report',        requires: 'can_view_reports' },
      { k: 'account',   icon: 'settings',  label: '내 계정',            href: '/account' },
      // 마을교육공동체(돌봄연계국 C) — 피처 플래그 활성 시 노출. 통합돌봄 운영 메뉴
      // 아래 하나의 "모듈" 항목으로 들어가고, 세부 섹션(7국·청소년·프로그램 등)은
      // 그 안의 탭이 담당한다. 설계상 '통합돌봄 모듈' 포지션을 사이드바에서 가시화.
      { g: '모듈', villageOnly: true },
      { k: 'village', icon: 'region', label: '마을교육공동체', href: '/village', villageOnly: true },
    ],
    // 유관기관(agency) 전용 내비. agency는 권역이 없어 region 내비를 차용하지 않고,
    // 페이지 가드를 모두 통과하는 항목(기관 스코프)만 노출한다 — 권역 대시보드/COD/
    // 승인/리포트/권역 관리는 제외(권한 없음 또는 권역 전용).
    agency: [
      { g: '운영' },
      { k: 'dashboard', icon: 'dashboard', label: '기관 대시보드',      href: '/dashboard/agency' },
      { k: 'schedule',  icon: 'schedule',  label: '일정',               href: '/schedule' },
      { k: 'map',       icon: 'map',       label: '지도',               href: '/map' },
      // 자원은행 30초 갱신 — 기관이 직접 가용성을 보고하는 유일한 쓰기 화면.
      { k: 'availability', icon: 'check', label: '가용성 갱신',        href: '/m/org', requires: 'can_update_org_availability' },
      { k: 'referrals',    icon: 'approve', label: '받은 의뢰',       href: '/m/referrals', requires: 'can_respond_referrals' },
      { g: '관리' },
      { k: 'org',       icon: 'org',       label: '유관기관',           href: '/organizations' },
      { k: 'manager',   icon: 'manager',   label: '케어매니저',         href: '/managers' },
      { g: '모듈', villageOnly: true },
      { k: 'village', icon: 'region', label: '마을교육공동체', href: '/village', villageOnly: true },
    ],
    platform: [
      { g: '플랫폼' },
      { k: 'dashboard', icon: 'dashboard', label: '플랫폼 대시보드',    href: '/dashboard/platform' },
      // 권역 관리(/regions)는 단일 권역(남양주) 전용 사이트라 사이드바에서 제외한다.
      // 화면·API 자체는 남아 있어 URL 직접 진입은 여전히 가능(권한 있는 경우).
      { k: 'admin',     icon: 'manager',   label: '관리자',             href: '/admins',        requires: 'can_manage_admins' },
      { k: 'manager',   icon: 'manager',   label: '케어매니저',         href: '/managers',      requires: 'can_manage_managers' },
      { k: 'members',   icon: 'user',      label: '회원 관리',          href: '/members',       requires: 'can_manage_members' },
      { k: 'audit',     icon: 'chart',     label: '감사 로그',          href: '/audit' },
      // 공개 페이지(도입 안내·고객지원)에서 접수된 문의함 — system_admin 전용 화면.
      { k: 'inquiries', icon: 'chat',      label: '문의 관리',          href: '/inquiries' },
      { g: '운영' },
      { k: 'approval',  icon: 'approve',   label: '승인 관리',          href: '/approval',      requires: 'can_approve' },
      { k: 'map',       icon: 'map',       label: '지도',               href: '/map' },
      { k: 'org',       icon: 'org',       label: '기관 마스터',        href: '/organizations', requires: 'can_manage_organizations' },
      { k: 'settings',  icon: 'settings',  label: '시스템 설정',        href: '/settings',      requires: 'can_manage_settings' },
      { k: 'uploads',   icon: 'upload',    label: '파일전송',           href: '/uploads',       requires: 'can_manage_settings' },
      { g: '모듈', villageOnly: true },
      { k: 'village', icon: 'region', label: '마을교육공동체', href: '/village', villageOnly: true },
    ],
    manager: [
      { g: '오늘' },
      { k: 'schedule',  icon: 'schedule',  label: '일정',               href: '/schedule' },
      { k: 'cases',     icon: 'case',      label: '담당 어르신',        href: '/cases' },
      { g: '업무' },
      { k: 'map',       icon: 'map',       label: '지도',               href: '/map' },
      { k: 'cod',       icon: 'match',     label: '매칭 제안',          href: '/cod' },
      { k: 'report',    icon: 'chart',     label: '방문 기록',          href: '/m/reports' },
    ],
    guardian: [
      // 보호자 내비는 GuardianTabBar(하단탭)가 실사용. 이 사이드바는 보조용이며,
      // 직원 화면(/schedule·/managers)이 아닌 보호자 전용 화면으로 연결한다.
      { k: 'dashboard', icon: 'heart',    label: '어르신 현황',         href: '/dashboard/guardian' },
      { k: 'schedule',  icon: 'schedule', label: '방문 일정',           href: '/m/timeline' },
      { k: 'manager',   icon: 'user',     label: '담당 매니저',         href: '/m/guardian-manager' },
      { k: 'chat',      icon: 'bell',     label: '알림함',              href: '/m/notify' },
    ],
    // 마을교육공동체(돌봄연계국 C) 섹션 전용 사이드바. VillageShell 이 /village 안에서만
    // propRole="village" 로 지정하면 이 메뉴로 교체된다. 통합돌봄 운영 메뉴로의 복귀는
    // 하단 "← 통합돌봄 메뉴" 항목 또는 Topbar 왼쪽 Logo(홈)에서 가능.
    // 아이템의 href 는 /village#tabkey 형태 — VillageShellInner 가 hashchange 로 탭 전환.
    village: [
      { g: '마을교육공동체' },
      { k: 'overview',  icon: 'region',   label: '7국 개요',          href: '/village#overview'  },
      { k: 'members',   icon: 'user',     label: '내 소속·승인',       href: '/village#members'   },
      { k: 'youths',    icon: 'heart',    label: '청소년',             href: '/village#youths'    },
      { k: 'programs',  icon: 'case',     label: '마을학교(PBL)',      href: '/village#programs'  },
      { k: 'safety',    icon: 'approve',  label: '민보 안전망',        href: '/village#safety'    },
      { k: 'motions',   icon: 'chat',     label: '공의 의회',          href: '/village#motions'   },
      { k: 'teachers',  icon: 'manager',  label: '마을교사',           href: '/village#teachers'  },
      { k: 'curricula', icon: 'chart',    label: '토의 교육과정',       href: '/village#curricula' },
      { k: 'map',       icon: 'map',      label: '지도',               href: '/village#map'       },
      { g: '통합돌봄' },
      { k: 'back',      icon: 'close',    label: '← 통합돌봄 메뉴',     href: '/launch'            },
    ],
  };
  const { user } = useCurrentUser();
  // 평상시엔 세션 역할이 사이드바를 결정하되, 섹션 셸(마을교육공동체 등)이 명시적으로
  // propRole 을 지정하면 그 섹션 전용 메뉴로 교체한다. 'village' 는 지금의 유일한 사례.
  const sectionOverride = propRole === 'village';
  const role = sectionOverride
    ? propRole
    : (user ? (SIDEBAR_ROLE_FOR_USER[user.role] || propRole) : propRole);
  const counts = user?.nav_counts || {};
  const caps = user?.capabilities || null;
  // If we haven't loaded the user yet (canvas demo, or session in flight),
  // show every item — same UX as PERMISSIVE_FLAGS in permissions.ts. Once
  // capabilities arrive, gated items hide for roles that lack the flag.
  // hideRoles: 권역 사이드바를 공유하지만 특정 화면이 성립하지 않는 역할에서 항목을
  // 숨긴다. 예: agency(유관기관 담당)는 권역이 없어 권역 대시보드가 흰 화면이 되므로 제외.
  // villageOnly: 마을교육공동체(돌봄연계국 C) 모듈 피처 플래그가 켜져 있을 때만 노출.
  const village = villageEnabled();
  const allow = (it) =>
    (!it.requires || !caps || caps[it.requires]) &&
    !(it.hideRoles && user && it.hideRoles.includes(user.role)) &&
    (!it.villageOnly || village);
  const items = (NAVS[role] || NAVS.region)
    .filter(it => it.g ? allow(it) : allow(it))
    .map(it => {
      if (it.g) return it;
      return counts[it.k] != null ? { ...it, count: counts[it.k] } : it;
    });

  // Display name + sub based on logged-in user, with fallback to demo defaults.
  const displayName = user?.name || (role === 'region' ? '김도현' : role === 'platform' ? '박상우' : role === 'manager' ? '이미경' : role === 'agency' ? '박혜진' : '박지영');
  const displaySub  = user?.role_label || (
    role === 'region' ? '서부권역 관리자' : role === 'platform' ? '시스템 관리자' :
    role === 'manager' ? '케어매니저' : role === 'agency' ? '유관기관 담당' : '보호자'
  );

  return (
    <aside className="sidebar">
      <Logo />
      {items.map((it, i) => it.g ? (
        <div key={i} className="nav-group">{it.g}</div>
      ) : (
        <a key={i} href={it.href || '#'} className={"nav-item" + (active === it.k ? " active" : "")} style={{textDecoration:'none', color:'inherit', cursor:'pointer'}}>
          <span className="ico">{Ic[it.icon]}</span>
          <span>{it.label}</span>
          {it.count != null && <span className="count">{it.count}</span>}
        </a>
      ))}
      <div style={{flex:1}}/>
      {/* 시스템 설정은 system_admin(can_manage_settings) 전용 화면이다. 능력이 없는
          역할에게 버튼을 노출하면 /settings 가드에 막혀 default_path로 튕긴다.
          allow()와 같은 컨벤션: 세션 로딩 전(caps null)엔 노출, 로딩 후엔 능력 기반. */}
      {(!caps || caps.can_manage_settings) && (
        <a href="/settings" className="nav-item" style={{opacity:.7, marginTop:8, textDecoration:'none', color:'inherit', cursor:'pointer'}}>
          <span className="ico">{Ic.settings}</span><span>설정</span>
        </a>
      )}
      <div onClick={logout} title="로그아웃" className="nav-item" style={{opacity:.7, color:'#EFB287', cursor:'pointer'}}>
        <span className="ico">{Ic.close}</span><span>로그아웃</span>
      </div>
      <div style={{display:'flex', gap:10, alignItems:'center', padding:'12px 8px 4px', borderTop:'1px solid rgba(255,255,255,.06)', marginTop:8}}>
        <div className="avatar sm" style={{background:'linear-gradient(135deg,#EFB287,#E8825B)', overflow:'hidden'}}>
          {user?.avatar_url
            ? <img src={user.avatar_url} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}}/>
            : displayName[0]}
        </div>
        <div style={{lineHeight:1.2, minWidth:0, flex:1, overflow:'hidden'}}>
          <div style={{fontSize:12, color:'#fff', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{displayName}</div>
          <div style={{fontSize:10, color:'#A89A7C', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{user?.region_name ? `${user.region_name} · ` : ''}{displaySub}</div>
        </div>
      </div>
    </aside>
  );
}

// 보호자/어르신 모바일 5탭 하단바. 화면마다 동일한 배열을 반복하면 새 탭 추가나
// 링크 수정 시 N곳을 고쳐야 하므로 한 곳으로 모음. active 키는 nav 'k' 값과 매치.
// 호출 예: <GuardianTabBar active="me"/>
const GUARDIAN_TABS = [
  { k: 'home',     l: '홈',    i: 'dashboard', href: '/dashboard/guardian' },
  { k: 'schedule', l: '일정',  i: 'schedule',  href: '/m/timeline'         },
  { k: 'manager',  l: '매니저', i: 'manager',  href: '/m/guardian-manager' },
  { k: 'notify',   l: '알림',  i: 'bell',      href: '/m/notify'           },
  { k: 'me',       l: '마이',  i: 'user',      href: '/m/guardian-me'      },
];

function GuardianTabBar({ active }) {
  return (
    <div data-tabbar="guardian" style={{display:'flex', justifyContent:'space-around', padding:'12px 16px 14px', borderTop:'1px solid var(--border-soft)', background:'var(--surface)'}}>
      {GUARDIAN_TABS.map((n) => (
        <a key={n.k} href={n.href}
           style={{textAlign:'center', color: active === n.k ? 'var(--orange-600)' : 'var(--ink-3)', textDecoration:'none'}}>
          <span style={{width:22, height:22, display:'inline-block'}}>{Ic[n.i]}</span>
          <div style={{fontSize:9, fontWeight:700, marginTop:2}}>{n.l}</div>
        </a>
      ))}
    </div>
  );
}

// 어르신 본인(citizen) 1인칭 하단 탭(5개: 홈·일정·복약·알림·내 정보). 보호자용
// GuardianTabBar(홈·일정·매니저·알림·마이) 대비 "매니저"는 홈 히어로/전화 버튼이
// 대신해 제거하고, 매일 챙기는 "복약"(/m/meds)을 탭으로 올린다. "도움말·상담센터
// 전화"는 별도 탭 없이 홈/내 정보 화면 안에서 닿게 한다. 스타일은 GuardianTabBar와 동일.
const ELDER_TABS = [
  { k: 'home',     l: '홈',     i: 'dashboard', href: '/home'           },
  { k: 'schedule', l: '일정',   i: 'schedule',  href: '/m/timeline'     },
  { k: 'meds',     l: '복약',   i: 'pill',      href: '/m/meds'         },
  { k: 'notify',   l: '알림',   i: 'bell',      href: '/m/notify'       },
  { k: 'me',       l: '내 정보', i: 'user',      href: '/m/guardian-me'  },
];

function ElderTabBar({ active }) {
  return (
    <div data-tabbar="elder" style={{display:'flex', justifyContent:'space-around', padding:'12px 16px 14px', borderTop:'1px solid var(--border-soft)', background:'var(--surface)'}}>
      {ELDER_TABS.map((n) => (
        <a key={n.k} href={n.href} aria-label={n.l}
           style={{textAlign:'center', color: active === n.k ? 'var(--orange-600)' : 'var(--ink-3)', textDecoration:'none'}}>
          <span style={{width:22, height:22, display:'inline-block'}}>{Ic[n.i]}</span>
          <div style={{fontSize:11, fontWeight:700, marginTop:2}}>{n.l}</div>
        </a>
      ))}
    </div>
  );
}

// 보호자/어르신 공용 모바일 화면(방문 일정·알림함 등)의 하단 탭바. 화면 자체는
// 같지만 chrome(하단 내비)은 로그인 역할에 맞춰야 한다 — citizen(어르신 본인)은
// ElderTabBar(4탭, /home 기준), guardian(보호자)은 GuardianTabBar(5탭).
// 역할 로딩 전(useCurrentUser in-flight)에는 기존 동작인 GuardianTabBar로 폴백.
// active 키는 두 탭바에 공통인 'schedule'·'notify'를 쓴다.
function RoleTabBar({ active }) {
  const { user } = useCurrentUser();
  return user?.role === "citizen"
    ? <ElderTabBar active={active} />
    : <GuardianTabBar active={active} />;
}

// ─── Self-service mutation helper ────────────────────────────
// 모바일 마이페이지(보호자/매니저/어르신)가 PATCH /users/me, /users/me/password를
// 같은 패턴으로 호출하므로 fetch + CSRF + 에러 정규화를 한 곳에 모았다.
// 비-2xx 응답은 백엔드의 details/message/error 중 가장 사람-읽기 좋은 한 줄을
// 골라 Error로 throw — 호출부는 catch에서 .message만 보면 된다.
function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}
async function patchJson(path, body) {
  const r = await fetch(path, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify(body),
  });
  if (r.status === 204) return null;
  const data = await r.json().catch(() => null);
  if (!r.ok) {
    const err: any = new Error(apiErrorMessage(data, r.status));
    err.code = data?.error;
    throw err;
  }
  return data;
}

function InlineAlert({ tone, children }) {
  // tone: 'success' | 'error'. success는 status, error는 alert role.
  const isSuccess = tone === "success";
  const bg = isSuccess ? "var(--green-50)"  : "var(--rose-50)";
  const fg = isSuccess ? "var(--green-700)" : "var(--rose-500)";
  return (
    <div role={isSuccess ? "status" : "alert"}
         style={{padding:'10px 12px', marginBottom:10, background:bg, color:fg, borderRadius:10, fontSize:12.5}}>{children}</div>
  );
}

// 신원 아바타 — 사진(avatar_url)이 있으면 원형 이미지로, 없으면 이름 이니셜로 폴백.
// 기존 `<div className="avatar …">{name[0]}</div>` 패턴을 그대로 대체하는 드롭인이다.
// className/style은 호출부의 크기·배경(gradient) 지정을 보존하고, 이미지가 있으면
// 배경 위를 cover로 덮는다. 케어 화면 곳곳(담당 매니저·어르신 카드)에서 공유한다.
function AvatarImg({ url, name, className = "avatar", style = undefined }) {
  const initial = (name && String(name)[0]) || "·";
  return (
    <div className={className} style={{ overflow: "hidden", ...(style || {}) }}>
      {url
        ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
        : initial}
    </div>
  );
}

// 프로필 사진 등록/교체/삭제 카드. 데스크톱 /account와 모바일 마이페이지(매니저·보호자·
// 어르신)가 공유한다. 파일 선택 즉시 멀티파트로 업로드(PATCH /users/me/avatar)하고,
// 성공 시 session:refresh로 Topbar/Sidebar/신원카드 아바타까지 갱신한다. 백엔드는
// require_login!만 걸려 모든 역할이 본인 사진을 올릴 수 있고, 타입(JPG·PNG·WebP)·
// 크기(5MB)는 AvatarAttachable concern이 검증한다. 미등록이면 이름 이니셜을 보여준다.
function AvatarEditor({ user }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState(null);
  const [ok, setOk]     = React.useState(null);
  const initial = (user?.name && user.name[0]) || "?";

  const upload = async (file) => {
    if (!file || busy) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const r = await fetch("/api/v1/users/me/avatar", {
        method: "PATCH",
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: fd,
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) throw new Error(apiErrorMessage(data, r.status));
      setOk("프로필 사진이 변경되었습니다.");
      window.dispatchEvent(new Event("session:refresh"));
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = ""; // 같은 파일 재선택 허용
    }
  };

  const remove = async () => {
    if (busy || !window.confirm("프로필 사진을 삭제할까요?")) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await fetch("/api/v1/users/me/avatar", {
        method: "DELETE",
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
      });
      if (!r.ok) {
        const data = await r.json().catch(() => null);
        throw new Error(apiErrorMessage(data, r.status));
      }
      setOk("프로필 사진이 삭제되었습니다.");
      window.dispatchEvent(new Event("session:refresh"));
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <>
      <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:6}}>프로필 사진</div>
      {err && <InlineAlert tone="error">{err}</InlineAlert>}
      {ok && <InlineAlert tone="success">{ok}</InlineAlert>}
      <div className="card" style={{padding:16, marginBottom:14, display:'flex', alignItems:'center', gap:16}}>
        <div className="avatar lg" data-avatar
             style={{width:64, height:64, fontSize:22, overflow:'hidden', background:'linear-gradient(135deg,#EFB287,#E8825B)'}}>
          {user?.avatar_url
            ? <img src={user.avatar_url} alt="프로필 사진" style={{width:'100%', height:'100%', objectFit:'cover'}}/>
            : initial}
        </div>
        <div style={{flex:1, minWidth:0}}>
          <div className="muted" style={{fontSize:12, lineHeight:1.6}}>JPG·PNG·WebP, 최대 5MB</div>
          <div style={{display:'flex', gap:8, marginTop:8}}>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                    className="btn btn-primary" style={{padding:'8px 14px', borderRadius:10, fontSize:12.5, fontWeight:700, cursor: busy?'default':'pointer'}}>
              {busy ? '처리 중…' : (user?.avatar_url ? '사진 변경' : '사진 등록')}
            </button>
            {user?.avatar_url && (
              <button type="button" onClick={remove} disabled={busy}
                      style={{padding:'8px 14px', background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, fontSize:12.5, fontWeight:600, cursor: busy?'default':'pointer'}}>삭제</button>
            )}
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{display:'none'}}
               onChange={e => upload(e.target.files?.[0])}/>
      </div>
    </>
  );
}

// 이름/전화 자가편집 카드. 보호자·어르신·매니저 마이페이지가 공유. read 모드는
// 라벨/값 + "편집 ›" 버튼이고, edit 모드는 인라인 form. 저장 성공 시
// session:refresh를 발사해 사이드바·헤더의 표시 이름도 같이 갱신된다.
function ProfileEditor({ user }) {
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({ name: "", phone: "" });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState(null);
  const [ok, setOk]     = React.useState(null);

  // edit 진입 순간만 user 값으로 seed — 진행 중인 입력을 user refresh가 덮어쓰지 않게.
  React.useEffect(() => {
    if (editing) {
      setForm({ name: user?.name || "", phone: user?.phone || "" });
      setErr(null); setOk(null);
    }
  }, [editing]);

  const save = async (e) => {
    e?.preventDefault();
    if (busy) return;
    // 연락처는 선택 입력이지만, 입력했다면 한국 전화번호 자릿수(9~11)를 만족해야 한다.
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length > 0 && (phoneDigits.length < 9 || phoneDigits.length > 11)) {
      setErr("연락처는 올바른 전화번호 형식이 아닙니다.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      await patchJson("/api/v1/users/me", { name: form.name.trim(), phone: form.phone.trim() });
      setOk("프로필이 저장되었습니다.");
      setEditing(false);
      window.dispatchEvent(new Event("session:refresh"));
    } catch (e2) {
      setErr(e2.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:6}}>프로필</div>
      {err && <InlineAlert tone="error">{err}</InlineAlert>}
      {ok && !editing && <InlineAlert tone="success">{ok}</InlineAlert>}
      {editing ? (
        <form className="card" style={{padding:14, marginBottom:14}} onSubmit={save}>
          <label style={{fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>이름</label>
          <input className="input" style={{width:'100%', marginTop:4, marginBottom:10}}
                 value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required/>
          <label style={{fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>연락처</label>
          <PhoneInput style={{width:'100%', marginTop:4}}
                 value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                 placeholder="010-1234-5678"/>
          <div style={{display:'flex', gap:8, marginTop:14}}>
            <button type="button" onClick={() => setEditing(false)} disabled={busy}
                    style={{flex:1, padding:'10px 0', background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, fontSize:13, fontWeight:600, cursor: busy?'default':'pointer'}}>취소</button>
            <button type="submit" disabled={busy}
                    className="btn btn-primary" style={{flex:1, padding:'10px 0', borderRadius:10, fontSize:13, fontWeight:700, cursor: busy?'default':'pointer'}}>
              {busy ? '저장 중…' : '저장'}
            </button>
          </div>
        </form>
      ) : (
        <div className="card" style={{padding:0, marginBottom:14, overflow:'hidden'}}>
          <div style={{display:'flex', padding:'12px 14px', borderBottom:'1px solid var(--border-soft)'}}>
            <div style={{flex:1, fontSize:12, color:'var(--ink-3)'}}>이름</div>
            <div style={{fontSize:13, fontWeight:600}}>{user?.name}</div>
          </div>
          <div style={{display:'flex', padding:'12px 14px', borderBottom:'1px solid var(--border-soft)'}}>
            <div style={{flex:1, fontSize:12, color:'var(--ink-3)'}}>연락처</div>
            <div style={{fontSize:13, fontWeight:600}}>{user?.phone || <span className="muted">미입력</span>}</div>
          </div>
          <button type="button" onClick={() => setEditing(true)}
                  style={{width:'100%', padding:'12px 14px', background:'transparent', border:'none', textAlign:'left', fontSize:13, fontWeight:600, color:'var(--orange-600)', cursor:'pointer'}}>
            프로필 편집 ›
          </button>
        </div>
      )}
    </>
  );
}

// 비밀번호 변경 카드. ProfileEditor와 같은 read/edit 패턴.
// 현재 비밀번호 검증 + 새 비밀번호 정책은 백엔드가 강제 — 여기는 confirm 일치만 사전 검사.
function PasswordChanger() {
  const [editing, setEditing] = React.useState(false);
  const [pw, setPw]   = React.useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState(null);
  const [ok, setOk]     = React.useState(null);

  React.useEffect(() => {
    if (editing) { setPw({ current: "", next: "", confirm: "" }); setErr(null); setOk(null); }
  }, [editing]);

  const save = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!pw.current || !pw.next) { setErr("현재 비밀번호와 새 비밀번호를 모두 입력해주세요."); return; }
    if (pw.next !== pw.confirm)  { setErr("새 비밀번호와 확인이 일치하지 않습니다."); return; }
    setBusy(true); setErr(null);
    try {
      await patchJson("/api/v1/users/me/password", {
        current_password: pw.current,
        password: pw.next,
        password_confirmation: pw.confirm,
      });
      setOk("비밀번호가 변경되었습니다.");
      setEditing(false);
    } catch (e2) {
      setErr(e2.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:6}}>보안</div>
      {err && <InlineAlert tone="error">{err}</InlineAlert>}
      {ok && !editing && <InlineAlert tone="success">{ok}</InlineAlert>}
      {editing ? (
        <form className="card" style={{padding:14, marginBottom:14}} onSubmit={save}>
          <label style={{fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>현재 비밀번호</label>
          <PasswordInput autoComplete="current-password" style={{marginTop:4, marginBottom:10}}
                 value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} required/>
          <label style={{fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>새 비밀번호</label>
          <PasswordInput autoComplete="new-password" style={{marginTop:4, marginBottom:4}}
                 value={pw.next} onChange={e => setPw(p => ({ ...p, next: e.target.value }))} required/>
          <div className="muted" style={{fontSize:10.5, marginBottom:10}}>최소 6자, 영문과 숫자를 모두 포함</div>
          <label style={{fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>새 비밀번호 확인</label>
          <PasswordInput autoComplete="new-password" style={{marginTop:4}}
                 value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} required/>
          <div style={{display:'flex', gap:8, marginTop:14}}>
            <button type="button" onClick={() => setEditing(false)} disabled={busy}
                    style={{flex:1, padding:'10px 0', background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, fontSize:13, fontWeight:600, cursor: busy?'default':'pointer'}}>취소</button>
            <button type="submit" disabled={busy}
                    className="btn btn-primary" style={{flex:1, padding:'10px 0', borderRadius:10, fontSize:13, fontWeight:700, cursor: busy?'default':'pointer'}}>
              {busy ? '변경 중…' : '비밀번호 변경'}
            </button>
          </div>
        </form>
      ) : (
        <div className="card" style={{padding:0, marginBottom:14, overflow:'hidden'}}>
          <button type="button" onClick={() => setEditing(true)}
                  style={{width:'100%', padding:'12px 14px', background:'transparent', border:'none', textAlign:'left', fontSize:13, fontWeight:600, color:'var(--ink-1)', cursor:'pointer', display:'flex', alignItems:'center'}}>
            <span style={{flex:1}}>비밀번호 변경</span>
            <span style={{color:'var(--ink-3)', fontSize:14}}>›</span>
          </button>
        </div>
      )}
    </>
  );
}

// 회원 탈퇴(자체) 카드 — 위험 구역. 어르신·보호자 마이페이지에만 배치한다.
// 비밀번호 재확인 + 명시적 확인 체크 후 DELETE /api/v1/users/me 호출.
// 서버가 즉시 로그인을 차단(state=off)하고 세션을 끊으므로 성공 시 /login으로 이동.
// 개인정보는 유예기간 후 자동 익명화 — 그 전까지는 관리자가 철회(재활성) 가능.
function AccountWithdraw() {
  const [open, setOpen]   = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [pw, setPw]       = React.useState("");
  const [agree, setAgree] = React.useState(false);
  const [busy, setBusy]   = React.useState(false);
  const [err, setErr]     = React.useState(null);

  React.useEffect(() => {
    if (open) { setReason(""); setPw(""); setAgree(false); setErr(null); }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!pw)    { setErr("현재 비밀번호를 입력해주세요."); return; }
    if (!agree) { setErr("안내를 확인하고 동의에 체크해주세요."); return; }
    if (!window.confirm("정말 탈퇴하시겠습니까?\n탈퇴 후에는 로그인할 수 없으며, 유예기간이 지나면 개인정보가 영구 삭제됩니다.")) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch("/api/v1/users/me", {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ current_password: pw, reason: reason.trim() }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => null);
        const detail = (Array.isArray(data?.details) && data.details.length && data.details.join(", "))
                    || data?.message || data?.error;
        throw new Error(detail || `HTTP ${r.status}`);
      }
      // 서버가 세션을 끊었다. 안내 후 로그인 화면으로.
      window.location.href = "/login";
    } catch (e2) {
      setErr(e2.message);
      setBusy(false);
    }
  };

  return (
    <>
      <div style={{fontSize:11, fontWeight:700, color:'var(--rose-500)', letterSpacing:'0.06em', marginBottom:6}}>회원 탈퇴</div>
      {err && <InlineAlert tone="error">{err}</InlineAlert>}
      {open ? (
        <form className="card" style={{padding:14, marginBottom:14, border:'1px solid var(--rose-200, var(--border-soft))'}} onSubmit={submit}>
          <div className="muted" style={{fontSize:11.5, lineHeight:1.7, marginBottom:12}}>
            탈퇴하면 즉시 로그인이 차단됩니다. 유예기간 동안에는 관리자에게 요청해 복구할 수 있으나,
            유예기간이 지나면 이름·연락처 등 개인정보가 <b>영구적으로 익명화</b>되어 복구할 수 없습니다.
          </div>
          <label style={{fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>탈퇴 사유 (선택)</label>
          <input className="input" style={{width:'100%', marginTop:4, marginBottom:10}}
                 value={reason} onChange={e => setReason(e.target.value)} placeholder="예) 서비스를 더 이상 이용하지 않음"/>
          <label style={{fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>현재 비밀번호</label>
          <PasswordInput autoComplete="current-password" style={{marginTop:4, marginBottom:10}}
                 value={pw} onChange={e => setPw(e.target.value)} required/>
          <label style={{display:'flex', alignItems:'flex-start', gap:8, fontSize:12, color:'var(--ink-2)', cursor:'pointer'}}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{marginTop:2}}/>
            <span>위 안내를 확인했으며, 탈퇴 및 개인정보 익명화에 동의합니다.</span>
          </label>
          <div style={{display:'flex', gap:8, marginTop:14}}>
            <button type="button" onClick={() => setOpen(false)} disabled={busy}
                    style={{flex:1, padding:'10px 0', background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, fontSize:13, fontWeight:600, cursor: busy?'default':'pointer'}}>취소</button>
            <button type="submit" disabled={busy}
                    style={{flex:1, padding:'10px 0', background:'var(--rose-500)', color:'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:700, cursor: busy?'default':'pointer'}}>
              {busy ? '처리 중…' : '탈퇴하기'}
            </button>
          </div>
        </form>
      ) : (
        <div className="card" style={{padding:0, marginBottom:14, overflow:'hidden'}}>
          <button type="button" onClick={() => setOpen(true)}
                  style={{width:'100%', padding:'12px 14px', background:'transparent', border:'none', textAlign:'left', fontSize:13, fontWeight:600, color:'var(--rose-500)', cursor:'pointer', display:'flex', alignItems:'center'}}>
            <span style={{flex:1}}>회원 탈퇴</span>
            <span style={{color:'var(--ink-3)', fontSize:14}}>›</span>
          </button>
        </div>
      )}
    </>
  );
}

// 알림 카테고리 on/off 토글 카드. GET 한 번으로 모든 카테고리를 받고, 각 토글은
// PATCH 한 건씩 보낸다 — 페이로드가 가벼우니 디바운싱 없이 즉시 반영해도 부담 없음.
// 백엔드가 라벨까지 제공하므로 한국어/영어 문구가 한 곳에서 관리된다.
function NotificationPreferences() {
  const [prefs, setPrefs] = React.useState(null);
  const [smsGlobal, setSmsGlobal] = React.useState(false);
  const [phonePresent, setPhonePresent] = React.useState(false);
  const [emailGlobal, setEmailGlobal] = React.useState(false);
  const [emailPresent, setEmailPresent] = React.useState(false);
  const [err, setErr]     = React.useState(null);
  const [busy, setBusy]   = React.useState({});  // `${category}:${channel}` → bool

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/v1/users/me/notification_preferences", { credentials: "same-origin" });
        const body = await r.json().catch(() => null);
        if (cancelled) return;
        if (!r.ok) { setErr(apiErrorMessage(body, r.status)); return; }
        setPrefs(body.preferences || []);
        setSmsGlobal(!!body.sms_enabled_globally);
        setPhonePresent(!!body.phone_present);
        setEmailGlobal(!!body.email_enabled_globally);
        setEmailPresent(!!body.email_present);
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // channel: "in_app"(=enabled 키) | "sms"(=sms 키) | "email"(=email 키). 채널마다 키가 달라 매핑.
  const toggle = async (category, channel, nextEnabled) => {
    const field = channel === "sms" ? "sms" : channel === "email" ? "email" : "enabled";
    const k = `${category}:${channel}`;
    if (busy[k]) return;
    setBusy(b => ({ ...b, [k]: true }));
    setErr(null);
    // optimistic — 즉시 토글 반영하고, 실패 시 롤백.
    setPrefs(ps => ps.map(p => p.category === category ? { ...p, [field]: nextEnabled } : p));
    try {
      await patchJson("/api/v1/users/me/notification_preferences", { category, channel, enabled: nextEnabled });
    } catch (e) {
      setPrefs(ps => ps.map(p => p.category === category ? { ...p, [field]: !nextEnabled } : p));
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(b => ({ ...b, [k]: false }));
    }
  };

  // SMS 열을 보여줄지: 전역 스위치가 켜져 있고(운영자가 SMS 발송을 연 상태) 본인 연락처가
  // 등록돼 있어야 의미가 있다. 둘 중 하나라도 빠지면 SMS 토글은 비활성 + 안내.
  const smsUsable = smsGlobal && phonePresent;
  // 이메일 열도 동일 논리: 전역 email_enabled + 본인 이메일 등록. SMS와 독립.
  const emailUsable = emailGlobal && emailPresent;

  return (
    <>
      <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:6}}>알림</div>
      {err && <InlineAlert tone="error">{err}</InlineAlert>}
      <div className="card" style={{padding:0, marginBottom:14, overflow:'hidden'}}>
        {prefs === null ? (
          <div className="muted" style={{padding:'14px', fontSize:12, textAlign:'center'}}>불러오는 중…</div>
        ) : prefs.length === 0 ? (
          <div className="muted" style={{padding:'14px', fontSize:12, textAlign:'center'}}>설정 가능한 알림 카테고리가 없습니다.</div>
        ) : (
          <>
            {/* 채널 헤더 — 앱(인앱) / 문자(SMS) / 메일(이메일) 세 열. */}
            <div style={{display:'flex', alignItems:'center', padding:'8px 14px', background:'var(--surface-2, #f7f7f8)', fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>
              <div style={{flex:1}}/>
              <div style={{width:48, textAlign:'center'}}>앱</div>
              {/* 문자(SMS) 채널 — 마스터 스위치(sms_enabled)가 켜지기 전엔 '준비중' 배지.
                  운영자가 실제 발송을 열면 smsGlobal=true가 되어 배지가 자동으로 사라진다. */}
              <div style={{width:48, textAlign:'center'}}>
                <div>문자</div>
                {!smsGlobal && <div style={{fontSize:8, fontWeight:700, color:'var(--orange-600, #c2620a)', marginTop:1, letterSpacing:0}}>준비중</div>}
              </div>
              {/* 메일(이메일) 채널 — sms와 동일 패턴. email_enabled 전역 스위치 연동. */}
              <div style={{width:48, textAlign:'center'}}>
                <div>메일</div>
                {!emailGlobal && <div style={{fontSize:8, fontWeight:700, color:'var(--orange-600, #c2620a)', marginTop:1, letterSpacing:0}}>준비중</div>}
              </div>
            </div>
            {prefs.map((p, i) => (
              <div key={p.category}
                   style={{display:'flex', alignItems:'center', padding:'12px 14px', borderTop:'1px solid var(--border-soft)'}}>
                <div style={{flex:1, fontSize:13, fontWeight:600}}>{p.label}</div>
                {/* 인앱 채널 토글(기존 동작). native checkbox로 접근성/키보드 유지. */}
                <div style={{width:48, textAlign:'center'}}>
                  <input type="checkbox" checked={!!p.enabled} disabled={!!busy[`${p.category}:in_app`]}
                         onChange={e => toggle(p.category, "in_app", e.target.checked)}
                         style={{width:36, height:20, accentColor:'var(--orange-500)'}}/>
                </div>
                {/* SMS 채널 토글. 전역 OFF 또는 연락처 미등록이면 비활성. */}
                <div style={{width:48, textAlign:'center'}}>
                  <input type="checkbox" checked={smsUsable && !!p.sms} disabled={!smsUsable || !!busy[`${p.category}:sms`]}
                         onChange={e => toggle(p.category, "sms", e.target.checked)}
                         style={{width:36, height:20, accentColor:'var(--orange-500)'}}/>
                </div>
                {/* 이메일 채널 토글. 전역 OFF 또는 이메일 미등록이면 비활성. */}
                <div style={{width:48, textAlign:'center'}}>
                  <input type="checkbox" checked={emailUsable && !!p.email} disabled={!emailUsable || !!busy[`${p.category}:email`]}
                         onChange={e => toggle(p.category, "email", e.target.checked)}
                         style={{width:36, height:20, accentColor:'var(--orange-500)'}}/>
                </div>
              </div>
            ))}
            {!smsUsable && (
              <div className="muted" style={{padding:'10px 14px', fontSize:11, borderTop:'1px solid var(--border-soft)'}}>
                {!smsGlobal ? '문자(SMS) 알림은 준비 중입니다. 곧 제공될 예정이에요.' : '문자 알림을 받으려면 내 정보에 연락처를 등록하세요.'}
              </div>
            )}
            {!emailUsable && (
              <div className="muted" style={{padding:'10px 14px', fontSize:11, borderTop:'1px solid var(--border-soft)'}}>
                {!emailGlobal ? '이메일 알림은 준비 중입니다. 곧 제공될 예정이에요.' : '이메일 알림을 받으려면 내 정보에 이메일을 등록하세요.'}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Topbar({ title, sub, role, children }) {
  const { user } = useCurrentUser();
  const initial = (user?.name && user.name[0]) || '?';
  return (
    <div className="topbar">
      <div style={{flex:'0 0 auto'}}>
        <div className="page-title">{title}</div>
        {sub && <div className="page-sub">{sub}</div>}
      </div>
      <div style={{flex:1}}/>
      {children}
      {/* 수정내역 보드(개발 추적) 진입점 — 시스템 관리자 헤더 전용. 사이드바가 아닌
          헤더에 상시 노출해 어느 관리 화면에서도 바로 진입할 수 있게 한다. */}
      {user?.role === 'system_admin' && (
        <a href="/dev-board" className="btn btn-ghost btn-sm" title="수정내역 보드"
          style={{textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6, color:'inherit'}}>
          {Ic.case}<span>수정내역</span>
        </a>
      )}
      <NotificationBell user={user}/>
      {/* 아바타 → 내 계정(본인 이름·연락처·비밀번호 수정). 데스크톱 운영 역할의
          self-service 진입점 — 사이드바 "내 계정" 메뉴와 동일 목적지. */}
      <a href="/account" title={`${user?.name || ''} · 내 계정`}
         style={{textDecoration:'none', display:'inline-flex'}}>
        <div className="avatar" style={{background:'linear-gradient(135deg,#EFB287,#E8825B)', cursor:'pointer', overflow:'hidden'}}>
          {user?.avatar_url
            ? <img src={user.avatar_url} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}}/>
            : initial}
        </div>
      </a>
    </div>
  );
}

// User.role → Notification.recipient_role. 권역/플랫폼 운영자는 admin 알림을 본다.
const NOTIFICATION_ROLE_FOR_USER = {
  manager:      'manager',
  guardian:     'guardian',
  citizen:      'guardian',
  region_admin: 'admin',
  coordinator:  'admin',   // 코디네이터도 관리 알림 채널 공유
  agency:       'admin',
  system_admin: 'admin',
};

const NOTIFICATION_POLL_MS = 60_000;

const NOTIF_SEVERITY_COLOR = { critical: 'rose', warning: 'amber', info: 'sky' };
const NOTIF_SEVERITY_LABEL = { critical: '중요', warning: '주의', info: '안내' };

function fmtNotifTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1)  return '방금';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)   return `${diffH}시간 전`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7)    return `${diffD}일 전`;
  return d.toLocaleDateString('ko-KR');
}

// 토프바 알림 진입점. 미로그인(캔버스/데모) 시 안전하게 0으로 표시.
// 데스크톱 전용 알림함 화면이 따로 없으므로 드롭다운 패널이 1차 진입점이며,
// "전체 보기"는 역할별 모바일 알림함(매니저 /m/notifications, 그 외 /m/notify)으로 연결한다.
function NotificationBell({ user }) {
  const role = user ? NOTIFICATION_ROLE_FOR_USER[user.role] : null;
  const [data, setData]   = React.useState(null);
  const [open, setOpen]   = React.useState(false);
  const [busy, setBusy]   = React.useState(false);
  const wrapRef = React.useRef(null);

  const load = React.useCallback(async () => {
    if (!role) { setData({ unread_count: 0, notifications: [] }); return; }
    try {
      const r = await fetch(`/api/v1/notifications?role=${role}`, { credentials: 'same-origin' });
      if (!r.ok) return;
      setData(await r.json());
    } catch { /* 네트워크 일시 오류는 무시 — 다음 폴링에서 회복 */ }
  }, [role]);

  React.useEffect(() => { load(); }, [load]);

  // 60s 자동 폴링(대시보드 폴링 간격과 일치) + 다른 화면의 액션 후 즉시 동기화.
  React.useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') load(); };
    const id = window.setInterval(tick, NOTIFICATION_POLL_MS);
    window.addEventListener('session:refresh', load);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('session:refresh', load);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  // 외부 클릭 / Esc로 패널 닫기.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markRead = React.useCallback(async (id) => {
    try { await fetch(`/api/v1/notifications/${id}/read`, { method: 'PATCH', credentials: 'same-origin' }); }
    catch { /* fall through to refresh */ }
    load();
  }, [load]);

  const markAllRead = React.useCallback(async () => {
    if (busy || !(data?.unread_count > 0)) return;
    setBusy(true);
    try {
      // 서버측 일괄 읽음 — 로드된 알림만이 아니라 스코프 전체 미읽음을 비운다(배지=0).
      await fetch('/api/v1/notifications/read_all', { method: 'PATCH', credentials: 'same-origin' });
      await load();
    } finally { setBusy(false); }
  }, [data, busy, load]);

  const unread = data?.unread_count ?? 0;
  const items  = (data?.notifications || []).slice(0, 8);

  return (
    <div ref={wrapRef} style={{position:'relative'}}>
      <button type="button" className="btn btn-ghost"
              aria-label={unread ? `알림 ${unread}건` : '알림'}
              aria-haspopup="true" aria-expanded={open}
              onClick={() => setOpen(o => !o)}
              style={{position:'relative', width:36, height:36, padding:0, flex:'0 0 auto'}}>
        {React.cloneElement(Ic.bell, { style: { width: 20, height: 20, display: 'block' } })}
        {unread > 0 && (
          <span style={{position:'absolute', top:2, right:2, transform:'translate(30%, -30%)',
                        minWidth:15, height:15, padding:'0 3px',
                        borderRadius:99, background:'var(--orange-500)', color:'#fff',
                        fontSize:10, fontWeight:800, display:'grid', placeItems:'center', lineHeight:1,
                        border:'1.5px solid var(--surface)', boxSizing:'border-box', pointerEvents:'none'}}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div role="dialog" aria-label="알림"
             style={{position:'absolute', top:'calc(100% + 8px)', right:0, width:360, maxHeight:480,
                     background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:12,
                     boxShadow:'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.12))',
                     display:'flex', flexDirection:'column', overflow:'hidden', zIndex:20}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between',
                       padding:'12px 16px', borderBottom:'1px solid var(--border-soft)'}}>
            <div style={{fontSize:13, fontWeight:700}}>
              알림{unread > 0 && <span style={{marginLeft:6, color:'var(--orange-600)'}}>{unread}건 미확인</span>}
            </div>
            <button type="button"
                    onClick={markAllRead}
                    disabled={busy || unread === 0}
                    style={{border:'none', background:'transparent', cursor: unread === 0 ? 'default' : 'pointer',
                            fontSize:11, fontWeight:600, color: unread === 0 ? 'var(--ink-4)' : 'var(--orange-600)'}}>
              모두 읽음
            </button>
          </div>

          <div style={{flex:1, overflowY:'auto'}}>
            {!data && <div className="muted" style={{padding:30, textAlign:'center', fontSize:12}}>불러오는 중…</div>}
            {data && items.length === 0 && (
              <div className="muted" style={{padding:30, textAlign:'center', fontSize:12}}>알림이 없습니다.</div>
            )}
            {items.map((n, i) => {
              const unreadItem = !n.read_at;
              const color = NOTIF_SEVERITY_COLOR[n.severity] || 'gray';
              const handle = (e) => {
                if (unreadItem) markRead(n.id);
                if (!n.link) { e.preventDefault(); setOpen(false); }
              };
              const Inner = (
                <div style={{display:'flex', gap:10, padding:'12px 16px',
                             borderTop: i > 0 ? '1px solid var(--border-soft)' : 'none',
                             background: unreadItem ? 'var(--orange-50)' : 'transparent'}}>
                  <span style={{width:6, flex:'0 0 6px', borderRadius:99,
                                background: unreadItem ? `var(--${color}-500)` : 'transparent', alignSelf:'stretch'}}/>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{display:'flex', gap:6, alignItems:'center', marginBottom:2}}>
                      <span className={"badge badge-" + color} style={{fontSize:9}}>{NOTIF_SEVERITY_LABEL[n.severity] || n.severity}</span>
                      <span className="muted" style={{fontSize:10}}>{fmtNotifTime(n.created_at)}</span>
                    </div>
                    <div style={{fontSize:13, fontWeight: unreadItem ? 700 : 500, lineHeight:1.35,
                                 overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical'}}>
                      {n.title}
                    </div>
                    {n.body && (
                      <div className="muted" style={{fontSize:11, marginTop:2, lineHeight:1.4,
                                                     overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical'}}>
                        {n.body}
                      </div>
                    )}
                  </div>
                </div>
              );
              return n.link ? (
                <a key={n.id} href={n.link} onClick={handle}
                   style={{textDecoration:'none', color:'inherit', display:'block', cursor:'pointer'}}>{Inner}</a>
              ) : (
                <div key={n.id} onClick={handle} style={{cursor:'pointer'}}>{Inner}</div>
              );
            })}
          </div>

          <div style={{padding:'10px 16px', borderTop:'1px solid var(--border-soft)', textAlign:'center'}}>
            {/* 역할별 알림함: 매니저=/m/notifications, 관리자군(admin 큐)=/notifications(데스크톱),
                보호자/어르신=NotifyCenter(/m/notify). 과거엔 관리자도 /m/notify로 보내 가드에
                막혀 대시보드로 튕겼다 — 관리자 전용 알림함으로 분기. */}
            <a href={user?.role === 'manager' ? '/m/notifications'
                     : NOTIFICATION_ROLE_FOR_USER[user?.role] === 'admin' ? '/notifications'
                     : '/m/notify'}
               style={{fontSize:12, fontWeight:600, color:'var(--ink-2)', textDecoration:'none'}}
               onClick={() => setOpen(false)}>
              전체 보기 →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// Pretty SVG line chart from data array.
// thresholds: 선택. [{ value, color }] — 각 값에 수평 점선 reference 라인을 그린다.
//   마지막 데이터 포인트가 임계치를 "초과"(current >= value)하면 점 색을 가장 엄격한
//   임계치 색으로 강조하고 반지름을 살짝 키운다.
//   thresholds 미전달이면 동작/외관 모두 기존과 100% 동일 — 모든 호출자 호환.
//   "높음=나쁨"이든 "낮음=나쁨"이든 호출 측에서 동등 임계 값을 전달하도록 통일
//   (호출자가 의미를 정하고, 컴포넌트는 표시만 책임짐).
function LineChart({ data, height = 120, color = "var(--orange-500)", fill = "var(--orange-50)", thresholds = null }) {
  const w = 100, h = 100;
  const thresholdValues = (thresholds || []).map(t => t.value).filter(v => Number.isFinite(v));
  // y축 도메인은 데이터 + 임계치 라인까지 포함해야 라인이 잘리지 않음.
  const max = Math.max(...data, ...thresholdValues);
  const min = Math.min(...data, ...thresholdValues);
  const range = max - min || 1;
  const yOf = v => h - ((v - min) / range) * h * 0.85 - 5;
  // 데이터가 1개면 (length-1)=0 으로 나눠 x가 NaN이 된다 → 최소 1로 가드.
  const denom = Math.max(1, data.length - 1);
  const pts = data.map((d, i) => [(i / denom) * w, yOf(d)]);
  const path = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0] + "," + p[1]).join(" ");
  const area = path + ` L${w},${h} L0,${h} Z`;
  const current = data.length ? data[data.length - 1] : null;
  const breached = (thresholds || [])
    .filter(t => current != null && current >= t.value)
    .sort((a, b) => b.value - a.value)[0];
  const pointColor = breached?.color || color;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <path d={area} fill={fill} />
      {(thresholds || []).map((t, i) => (
        <line key={`th-${i}`} x1={0} x2={w} y1={yOf(t.value)} y2={yOf(t.value)}
              stroke={t.color || "var(--ink-3)"} strokeWidth="0.6"
              strokeDasharray="2 2" opacity="0.6"
              vectorEffect="non-scaling-stroke" />
      ))}
      <path d={path} stroke={color} strokeWidth="1.2" fill="none" vectorEffect="non-scaling-stroke" />
      {pts.map((p, i) => i === pts.length - 1 && (
        <circle key={i} cx={p[0]} cy={p[1]} r={breached ? 1.8 : 1.2} fill={pointColor} />
      ))}
    </svg>
  );
}

// Simple bar chart
function BarChart({ data, height = 120, color = "var(--green-500)" }) {
  const w = 100, h = 100;
  // 데이터가 전부 0이거나 비면 max=0/-Infinity → (d/max)가 NaN이 되어
  // <rect y="NaN" height="NaN"> 경고가 난다. 최소 1로 가드해 0 높이 막대로 안전 렌더.
  const max = Math.max(1, ...data);
  const bw = data.length ? w / data.length : w;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      {data.map((d, i) => {
        const bh = (d / max) * h * 0.85;
        return <rect key={i} x={i * bw + 1} y={h - bh - 2} width={bw - 2} height={bh} fill={color} rx="0.6" />;
      })}
    </svg>
  );
}

function Kpi({ label, value, delta, deltaDir, sparkline, color }) {
  return (
    <div className="card kpi">
      <div className="lbl">{label}</div>
      <div style={{display:'flex', alignItems:'baseline', gap:8}}>
        <div className="val">{value}</div>
        {delta && <div className={"delta " + (deltaDir || 'up')}>{deltaDir === 'down' ? '↓' : '↑'} {delta}</div>}
      </div>
      {sparkline && <div style={{marginTop:-6, marginLeft:-4, marginRight:-4}}><LineChart data={sparkline} height={36} color={color || "var(--orange-500)"} fill={(color || "var(--orange-500)").replace('500','50')} /></div>}
    </div>
  );
}

// ─── Shared footer ──────────────────────────────────────
function AppFooter() {
  const village = villageEnabled();
  return (
    // 폰 폭에서는 두 그룹이 나란히 붙으면 서로 밀려 글자가 중간에 끊긴다.
    // 좁은 화면 스택 전환·구분점 숨김은 .app-footer 미디어쿼리(design-tokens.css)에서 처리.
    <footer className="app-footer">
      <div className="app-footer-group">
        <a href="/" aria-label={`${siteName()} 홈`} style={{textDecoration:'none'}}>
          <img src={BRAND_IMG.mark} alt="" width={22} height={22}
               style={{width:22, height:22, borderRadius:6, objectFit:'cover', display:'block'}}/>
        </a>
        <span>© 2026 <b style={{color:'var(--ink-2)'}}>{siteName()}</b></span>
        <span className="app-footer-sep" style={{color:'var(--ink-4)'}}>·</span>
        <span>사업자등록번호 123-45-67890</span>
      </div>
      <div className="app-footer-group app-footer-links">
        <a href="/privacy" style={{color:'inherit', textDecoration:'none'}}>개인정보처리방침</a>
        <a href="/terms"   style={{color:'inherit', textDecoration:'none'}}>이용약관</a>
        {village && (
          <a href="/village" style={{color:'inherit', textDecoration:'none'}}>마을교육공동체</a>
        )}
        <a href="mailto:support@care.local" style={{color:'inherit', textDecoration:'none'}}>support@care.local</a>
        <span>고객지원 <b style={{color:'var(--ink-2)'}}>{supportPhone()}</b></span>
      </div>
    </footer>
  );
}

// Compact one-line footer for tight spaces (mobile).
function AppFooterCompact() {
  const village = villageEnabled();
  return (
    <div style={{
      flex: '0 0 auto',
      padding: '8px 22px 6px',
      fontSize: 9.5, color: 'var(--ink-3)', textAlign: 'center',
      background: 'var(--surface-2)',
    }}>
      © 2026 {siteName()} · {supportPhone()} · <a href="/privacy" style={{color:'inherit'}}>처리방침</a> · <a href="/terms" style={{color:'inherit'}}>약관</a>
      {village && <> · <a href="/village" style={{color:'inherit'}}>마을교육공동체</a></>}
    </div>
  );
}

// ── Vital severity 임계치 ─────────────────────────────────────────────────
// 매니저 모바일·데스크톱·운영자 집계가 같은 기준을 쓰도록 한 곳에 모은다.
// Ruby 쪽 app/models/concerns/vital_severity.rb 와 동일한 분류여야 하며,
// 변경 시 양쪽을 함께 갱신해야 한다 — 그래야 운영자 통계와 매니저 화면이
// 같은 row 를 비정상으로 분류한다.
// 반환값: 'critical' | 'warning' | 'normal' | null(미측정).
function bpSeverity(bp) {
  if (bp == null) return null;
  // 호출부는 두 가지 형태로 들어온다:
  //  - "142/86" 문자열 (모바일 카드의 활력 표시 입력 등)
  //  - { sys, dia } 객체 (API row 직렬값)
  let sys, dia;
  if (typeof bp === "string") {
    const [s, d] = bp.split("/");
    sys = parseInt(s, 10); dia = parseInt(d, 10);
  } else if (typeof bp === "object") {
    sys = Number(bp.sys); dia = Number(bp.dia);
  }
  if (!Number.isFinite(sys) || !Number.isFinite(dia)) return null;
  if (sys >= 160 || sys < 80 || dia >= 100) return "critical";
  if (sys >= 140 || sys < 90 || dia >= 90 || dia < 60) return "warning";
  return "normal";
}
function hrSeverity(hr) {
  const n = Number(hr);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 50 || n > 120) return "critical";
  if (n < 60 || n > 100) return "warning";
  return "normal";
}
function tempSeverity(t) {
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 38.5 || n < 35.5) return "critical";
  if (n >= 37.5 || n < 36.0) return "warning";
  return "normal";
}
function vitalColorVar(severity) {
  if (severity === "critical") return "var(--rose-600)";
  if (severity === "warning")  return "var(--amber-600)";
  if (severity === "normal")   return "var(--green-600)";
  return "var(--ink-3)";
}

// 매니저↔보호자 메시지 작성 박스. POST /api/v1/messages 로 보낸다.
// - elderlyId 가 주어지면(매니저 측) 대상 어르신을 함께 전송한다.
// - 보호자 측은 생략 — 백엔드가 담당 매니저를 자동으로 결정한다.
// 수신자는 알림함(NotifyCenter / NotificationBell)에서 kind:"message" 로 본다.
function MessageComposer({ elderlyId = null, toLabel = "메시지 보내기", placeholder = "메시지를 입력하세요…" }) {
  const MAX = 2000;
  const [body, setBody]       = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [done, setDone]       = React.useState(false);
  const [err, setErr]         = React.useState(null);

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true); setErr(null);
    try {
      const r = await fetch("/api/v1/messages", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ body: text, ...(elderlyId ? { elderly_id: elderlyId } : {}) }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) { setErr(j?.message || `전송 실패 (HTTP ${r.status})`); return; }
      setBody(""); setDone(true);
      setTimeout(() => setDone(false), 2500);
    } catch (e) {
      setErr(e.message || "전송에 실패했습니다.");
    } finally { setSending(false); }
  };

  const disabled = sending || !body.trim();
  return (
    <div className="card" style={{padding:14, marginBottom:14}}>
      <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:8}}>{toLabel}</div>
      <textarea value={body} maxLength={MAX} rows={3} placeholder={placeholder}
        onChange={e => setBody(e.target.value)}
        style={{width:'100%', boxSizing:'border-box', resize:'vertical', padding:'10px 12px',
                borderRadius:10, border:'1px solid var(--border)', fontSize:13, fontFamily:'inherit',
                background:'var(--surface)', color:'var(--ink-1)'}}/>
      {err && <div role="alert" style={{marginTop:8, fontSize:12, color:'var(--rose-500)'}}>{err}</div>}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:8}}>
        <span style={{fontSize:11, color: done ? 'var(--green-600)' : 'var(--ink-3)'}}>
          {done ? '✓ 전송되었습니다' : `${body.length}/${MAX}`}
        </span>
        <button type="button" onClick={send} disabled={disabled} className="btn btn-primary btn-sm"
          style={{padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:700,
                  opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer'}}>
          {sending ? '전송 중…' : '보내기'}
        </button>
      </div>
    </div>
  );
}

// ─── 알림 피드 (보호자/매니저 공용) ──────────────────────────
// NotifyCenter(보호자)·MgrNotify(매니저)가 공유하는 알림 목록 본문. 상단 상태바/
// 하단 탭바 등 chrome은 호출 화면이 감싸고, 여기서는 헤더 액션(뒤로·모두읽음·
// 읽은것삭제) + 필터칩 + 날짜 그룹 목록만 그린다. 반환은 프래그먼트(app 컨테이너
// 미포함) — 부모가 <div className="app"> flex 컬럼으로 감싼다.
// API(/api/v1/notifications)가 current_user 신원으로 스코프하므로 role 파라미터는
// 보내지 않는다 — 보호자는 자기 어르신, 매니저는 자기 매니저 알림만 내려온다.
const NOTIFY_SEV_COLOR = { critical: "rose", warning: "amber", info: "green" };
const NOTIFY_SEV_ICON  = { critical: "alert", warning: "alert", info: "check" };

function notifySameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// elder=true: 어르신용 큰 카드(아이콘 44px·제목 15px·본문 13px) + 큰 × 삭제(40px),
// 카드 전체 탭 → onItemClick(읽음+이동). 보호자/매니저는 기존(작은 카드 + × 삭제).
function NotifyGroup({ title, items, dim, onDelete, elder, onItemClick }) {
  return (
    <>
      <div style={{fontSize: elder ? 13 : 11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', margin: elder ? '14px 0 8px' : '10px 0 8px'}}>{title}</div>
      {items.map(n => {
        const c = NOTIFY_SEV_COLOR[n.severity];
        const important = n.severity === 'critical';
        const isNew = !n.read_at;
        // 어르신 모드뿐 아니라 표준 알림함(보호자·매니저)도 카드 탭 → 읽음+link 이동.
        // (메시지 알림 link가 /m/chat 스레드를 가리켜, 알림에서 바로 대화로 진입.)
        const clickable = !!onItemClick;
        // 읽음 점은 카드 우상단에 그대로 두고, × 버튼은 카드 본문 흐름의 마지막 flex
        // 아이템으로 두어 충돌 방지. × 클릭은 부모 카드 클릭(향후 link 이동 등)과
        // 분리되도록 stopPropagation.
        return (
          <div key={n.id} className="card" data-notif-card={n.id}
               onClick={clickable ? () => onItemClick(n) : undefined}
               role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined}
               aria-label={clickable ? `${n.title}${isNew ? ', 새 알림' : ''}` : undefined}
               style={{padding: elder ? '16px' : '14px', marginBottom:8, display:'flex', gap: elder ? 12 : 10, position:'relative', alignItems:'flex-start', border: important?'1.5px solid var(--rose-300)':'1px solid var(--border-soft)', opacity: dim ? 0.85 : 1, cursor: clickable ? 'pointer' : undefined}}>
            {isNew && <span style={{position:'absolute', top: elder ? 16 : 14, right: elder ? 16 : 14, width: elder ? 10 : 8, height: elder ? 10 : 8, borderRadius:99, background:'var(--orange-500)'}}/>}
            <span style={{width: elder ? 44 : 36, height: elder ? 44 : 36, borderRadius:elder ? 12 : 10, background:`var(--${c}-50)`, color:`var(--${c}-600)`, display:'grid', placeItems:'center', flex:'0 0 auto'}}>
              <span style={{width: elder ? 22 : 18, height: elder ? 22 : 18, display:'inline-block'}}>{Ic[NOTIFY_SEV_ICON[n.severity]]}</span>
            </span>
            <div style={{flex:1, minWidth:0, paddingRight: isNew ? (elder ? 16 : 14) : 0}}>
              <div style={{fontSize: elder ? 15 : 13, fontWeight:700}}>{n.title}</div>
              {n.body && <div className="muted" style={{fontSize: elder ? 13 : 11, marginTop:2, lineHeight:1.45}}>{n.body}</div>}
              <div className="muted" style={{fontSize: elder ? 13 : 10, marginTop:6}}>{new Date(n.created_at).toLocaleTimeString('ko-KR',{hour: elder ? 'numeric' : '2-digit', minute:'2-digit'})}</div>
            </div>
            {onDelete && (
              <button type="button"
                      onClick={(e) => { e.stopPropagation(); onDelete(n.id); }}
                      aria-label={`'${n.title}' 알림 삭제`}
                      style={{alignSelf:'center', width: elder ? 40 : 28, height: elder ? 40 : 28, padding:0, borderRadius: elder ? 12 : 8, background:'transparent', border: elder ? '1px solid var(--border)' : 'none', color:'var(--ink-3)', cursor:'pointer', display:'grid', placeItems:'center', flex:'0 0 auto'}}>
                <span style={{width: elder ? 18 : 14, height: elder ? 18 : 14, display:'inline-block'}}>{Ic.close}</span>
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}

// 알림 읽음/삭제로 미읽음 수가 바뀐 직후 호출. 같은 문서 안의 배지(예: 같은
// 페이지에 떠 있는 카운트)가 즉시 반영되도록 앱-내 이벤트를 방송한다. 페이지를
// 건너뛰는 동기화(뒤로가기 등)는 useUnreadCount의 pageshow 경로가 담당한다.
function emitNotificationsChanged() {
  window.dispatchEvent(new Event("notifications:changed"));
}

// 미읽음 배지용 카운트 훅. 마운트 1회 fetch만 하면 bfcache(브라우저 뒤로가기
// 복원) 시 컴포넌트가 재마운트되지 않아 카운트가 읽기 전 값으로 멈춘다 —
// "새로고침해야 반영"되던 버그. pageshow(bfcache 복원 포함)·탭 재포커스
// (visibilitychange)·앱-내 notifications:changed 에 모두 재요청해 항상 최신화한다.
// API는 current_user 신원으로 스코프하므로 role 파라미터는 불필요.
function useUnreadCount() {
  const [unread, setUnread] = React.useState(0);
  const load = React.useCallback(() => {
    fetch("/api/v1/notifications", { credentials: "same-origin" })
      .then(r => (r.ok ? r.json() : null))
      .then(n => { if (n) setUnread(n.unread_count || 0); })
      .catch(() => { /* 일시 오류 무시 — 다음 신호에서 회복 */ });
  }, []);
  React.useEffect(() => {
    load();
    const onShow = () => load();                                   // bfcache 복원 포함
    const onVis  = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("pageshow", onShow);
    window.addEventListener("notifications:changed", onShow);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pageshow", onShow);
      window.removeEventListener("notifications:changed", onShow);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);
  return unread;
}

function NotifyFeed() {
  const [data, setData] = React.useState(null);
  const [filter, setFilter] = React.useState(null); // null | 'critical' | 'visit_start' etc.
  const [busy, setBusy] = React.useState(false);    // bulk 액션 진행 표시
  const [err, setErr] = React.useState(null);
  // 어르신(citizen)은 필터·일괄/개별 삭제 같은 "관리" 기능을 숨기고, 큰 카드 +
  // 탭하면 읽음 처리만 노출한다(파괴적 동작 제거). 보호자/매니저는 기존 그대로.
  // 설계: docs/elder-screens-design.md 4.3
  const { user } = useCurrentUser();
  const isElder = user?.role === "citizen";

  const [loadingMore, setLoadingMore] = React.useState(false);
  const load = () => fetch("/api/v1/notifications", { credentials: "same-origin" })
    .then(r => r.json()).then(setData);
  React.useEffect(() => { load(); }, []);

  // 페이지네이션: 마지막으로 받은 알림 id를 before 커서로 다음 PAGE_SIZE개를 이어 붙인다.
  // 서버가 has_more=false면 끝. 배지(unread_count)는 항상 서버 응답의 최신값으로 갱신.
  const loadMore = async () => {
    if (loadingMore || !data?.has_more || !data.notifications.length) return;
    setLoadingMore(true); setErr(null);
    try {
      const last = data.notifications[data.notifications.length - 1];
      const r = await fetch(`/api/v1/notifications?before=${last.id}`, { credentials: "same-origin" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const next = await r.json();
      setData(d => ({ ...next, notifications: [...d.notifications, ...next.notifications] }));
    } catch {
      setErr("더 불러오지 못했습니다. 다시 시도해주세요.");
    } finally { setLoadingMore(false); }
  };

  // 어르신 모드: 카드 탭 → 안 읽음이면 읽음 처리하고, link 있으면 이동.
  const markReadAndGo = async (n) => {
    if (!n.read_at) {
      setData(d => ({ ...d, notifications: d.notifications.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x) }));
      try {
        await fetch(`/api/v1/notifications/${n.id}/read`, { method: "PATCH", credentials: "same-origin" });
        emitNotificationsChanged();
      } catch { /* 표시용 낙관적 갱신 — 실패해도 이동은 진행 */ }
    }
    if (n.link) window.location.href = n.link;
  };

  const markAllRead = async () => {
    if (!data || busy) return;
    setBusy(true); setErr(null);
    try {
      // 서버측 일괄 읽음 — 로드된 페이지가 아니라 스코프 전체 미읽음을 비운다(배지=0).
      await fetch("/api/v1/notifications/read_all", { method: "PATCH", credentials: "same-origin" });
      await load();
      emitNotificationsChanged();
    } finally { setBusy(false); }
  };

  // 개별 삭제. confirm으로 의도 확인 후 optimistic 제거 — 실패 시 prev로 롤백.
  // 메시지에 알림 제목을 노출해 무엇을 지우는지 알 수 있게 한다.
  const deleteOne = async (id) => {
    if (!data) return;
    const n = data.notifications.find(x => x.id === id);
    if (!n) return;
    if (!window.confirm(`"${n.title}" 알림을 삭제하시겠습니까?`)) return;
    const prev = data;
    setErr(null);
    setData(d => ({ ...d, notifications: d.notifications.filter(x => x.id !== id) }));
    try {
      const r = await fetch(`/api/v1/notifications/${id}`, { method: "DELETE", credentials: "same-origin" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      emitNotificationsChanged();
    } catch {
      setData(prev);
      setErr("삭제에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const deleteRead = async () => {
    if (!data || busy) return;
    const readCount = data.notifications.filter(n => n.read_at).length;
    if (readCount === 0) { setErr("삭제할 읽은 알림이 없습니다."); return; }
    if (!window.confirm(`읽은 알림 ${readCount}건을 삭제하시겠습니까?`)) return;
    setBusy(true); setErr(null);
    const prev = data;
    setData(d => ({ ...d, notifications: d.notifications.filter(n => !n.read_at) }));
    try {
      const r = await fetch(`/api/v1/notifications/read`, { method: "DELETE", credentials: "same-origin" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch {
      setData(prev);
      setErr("일괄 삭제에 실패했습니다.");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="muted" style={{padding:40}}>불러오는 중…</div>;

  const all = data.notifications;
  const visible = filter ? all.filter(n => filter === 'critical' ? n.severity === 'critical' : n.kind?.startsWith(filter)) : all;
  const groupByDate = (date) => visible.filter(n => notifySameDay(new Date(n.created_at), date));
  const todayItems = groupByDate(new Date());
  const yesterdayItems = groupByDate(new Date(Date.now() - 86400000));
  const olderItems = visible.filter(n => {
    const d = new Date(n.created_at);
    return !notifySameDay(d, new Date()) && !notifySameDay(d, new Date(Date.now() - 86400000));
  });
  const hasRead = all.some(n => n.read_at);

  // ── 어르신(citizen) 알림 — 큰 카드 + 탭하면 읽음. 개별 삭제(큰 ×) + 읽은 것 삭제. ──
  if (isElder) {
    const unread = all.filter(n => !n.read_at).length;
    return (
      <>
        <div style={{padding:'8px 22px 6px', display:'flex', alignItems:'center', gap:12}}>
          <a onClick={() => history.back()} aria-label="뒤로"
             style={{width:40, height:40, display:'grid', placeItems:'center', borderRadius:12, background:'var(--surface)', color:'var(--ink-1)', fontSize:22, cursor:'pointer', textDecoration:'none'}}>‹</a>
          <div style={{fontSize:21, fontWeight:800, flex:1}}>알림</div>
          {unread > 0 && (
            <span role="button" tabIndex={0} onClick={markAllRead} aria-disabled={busy}
                  style={{fontSize:14, color: busy ? 'var(--ink-3)' : 'var(--orange-600)', fontWeight:700, cursor: busy ? 'default' : 'pointer', whiteSpace:'nowrap'}}>모두 읽음</span>
          )}
          {hasRead && (
            <span role="button" tabIndex={0} onClick={busy ? undefined : deleteRead} aria-disabled={busy}
                  style={{fontSize:14, color: busy ? 'var(--ink-3)' : 'var(--rose-500)', fontWeight:700, cursor: busy ? 'default' : 'pointer', whiteSpace:'nowrap'}}>읽은 것 삭제</span>
          )}
        </div>
        {unread > 0 && <div style={{padding:'0 22px 6px', fontSize:13, color:'var(--ink-3)'}}>새 소식 {unread}건</div>}
        {err && (
          <div role="alert" style={{margin:'0 22px 8px', padding:'12px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:12, fontSize:14}}>{err}</div>
        )}

        <div style={{padding:'8px 22px 14px', flex:1, overflow:'auto'}}>
          {visible.length === 0 && <div style={{fontSize:15, color:'var(--ink-3)', padding:'40px 0', textAlign:'center'}}>아직 새 소식이 없어요.</div>}
          {todayItems.length > 0     && <NotifyGroup title="오늘" items={todayItems}     elder onItemClick={markReadAndGo} onDelete={deleteOne}/>}
          {yesterdayItems.length > 0 && <NotifyGroup title="어제" items={yesterdayItems} elder onItemClick={markReadAndGo} onDelete={deleteOne} dim/>}
          {olderItems.length > 0     && <NotifyGroup title="지난 소식" items={olderItems} elder onItemClick={markReadAndGo} onDelete={deleteOne} dim/>}
          {data.has_more && (
            <div style={{padding:'8px 0 4px', textAlign:'center'}}>
              <span role="button" tabIndex={0} onClick={loadMore} aria-disabled={loadingMore}
                    style={{display:'inline-block', padding:'12px 24px', borderRadius:12, background:'var(--surface)', border:'1px solid var(--border)', fontSize:15, fontWeight:700, color: loadingMore ? 'var(--ink-3)' : 'var(--ink-1)', cursor: loadingMore ? 'default' : 'pointer'}}>
                {loadingMore ? '불러오는 중…' : '이전 소식 더 보기'}
              </span>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{padding:'8px 22px 12px', display:'flex', alignItems:'center', gap:12}}>
        <a onClick={() => history.back()} style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', cursor:'pointer', textDecoration:'none'}}>‹</a>
        <div style={{fontSize:16, fontWeight:800, flex:1}}>알림</div>
        {/* 두 액션 모두 텍스트 링크 스타일. 읽은 게 없으면 '읽은 것 삭제'는 비활성. */}
        <span role="button" tabIndex={0} onClick={markAllRead}
              aria-disabled={busy}
              style={{fontSize:11, color: busy ? 'var(--ink-3)' : 'var(--orange-600)', fontWeight:600, cursor: busy ? 'default' : 'pointer'}}>모두 읽음</span>
        <span role="button" tabIndex={0} onClick={hasRead && !busy ? deleteRead : undefined}
              aria-disabled={!hasRead || busy}
              style={{fontSize:11, color: (!hasRead || busy) ? 'var(--ink-3)' : 'var(--rose-500)', fontWeight:600, cursor: (!hasRead || busy) ? 'default' : 'pointer'}}>읽은 것 삭제</span>
      </div>

      {err && (
        <div role="alert" style={{margin:'0 22px 8px', padding:'10px 12px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>
      )}

      <div style={{padding:'4px 22px 0', display:'flex', gap:6, marginBottom:14, flexWrap:'wrap'}}>
        {[
          // k는 kind prefix(또는 'critical'=severity). visible 필터가 startsWith로 매칭.
          { l: '전체', k: null, n: all.length },
          { l: '중요', k: 'critical', n: all.filter(n=>n.severity==='critical').length },
          { l: '방문', k: 'visit', n: all.filter(n=>n.kind?.startsWith('visit')).length },
          { l: '복약', k: 'medication', n: all.filter(n=>n.kind?.startsWith('medication')).length },
          { l: '메시지', k: 'message', n: all.filter(n=>n.kind?.startsWith('message')).length },
        ].map((t, i) => {
          const active = filter === t.k;
          return (
            <span key={i} onClick={()=>setFilter(t.k)} style={{padding:'6px 12px', borderRadius:99, background: active?'var(--orange-500)':'var(--bg-sub)', color: active?'#fff':'var(--ink-2)', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:5}}>
              {t.l}<span style={{padding:'0 6px', fontSize:10, borderRadius:99, background: active?'rgba(255,255,255,0.25)':'var(--border)', color:'inherit'}}>{t.n}</span>
            </span>
          );
        })}
      </div>

      <div style={{padding:'0 22px 14px', flex:1, overflow:'auto'}}>
        {visible.length === 0 && <div className="muted" style={{fontSize:12, padding:'40px 0', textAlign:'center'}}>알림이 없습니다.</div>}
        {todayItems.length > 0     && <NotifyGroup title="오늘" items={todayItems}     onDelete={deleteOne} onItemClick={markReadAndGo}/>}
        {yesterdayItems.length > 0 && <NotifyGroup title="어제" items={yesterdayItems} onDelete={deleteOne} onItemClick={markReadAndGo} dim/>}
        {olderItems.length > 0     && <NotifyGroup title="이전" items={olderItems}     onDelete={deleteOne} onItemClick={markReadAndGo} dim/>}
        {data.has_more && (
          <div style={{padding:'4px 0 8px', textAlign:'center'}}>
            <span role="button" tabIndex={0} onClick={loadMore} aria-disabled={loadingMore}
                  style={{display:'inline-block', padding:'9px 18px', borderRadius:10, background:'var(--surface)', border:'1px solid var(--border)', fontSize:12, fontWeight:600, color: loadingMore ? 'var(--ink-3)' : 'var(--ink-1)', cursor: loadingMore ? 'default' : 'pointer'}}>
              {loadingMore ? '불러오는 중…' : '이전 알림 더 보기'}
            </span>
          </div>
        )}
      </div>
    </>
  );
}

// 검색형 소속기관 드롭다운. care 기관이 수천 건이라 native <select>(전체 옵션을
// 한꺼번에 렌더)는 마운트·재렌더가 무겁고 사용성도 나쁘다. 이름을 입력해 좁히고
// 매칭 상위 N개만 렌더한다(이미 메모리에 있는 목록을 클라이언트 필터 — 추가 API 없음).
// 권역관리자 케어매니저 관리(management.tsx)와 회원가입(signup.tsx)이 공유한다.
//   value: 선택된 기관 id(number) | null,  onChange(id|null)
//   allLabel: 지정 시 "전체/해제" 항목을 맨 위에 노출(필터용). 미지정 시 미선택='— 선택 —'.
const ORG_COMBO_LIMIT = 50;
function OrgCombobox({ orgs, value, onChange, allLabel, placeholder, width = 200, invalid = false }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const boxRef = React.useRef(null);

  const selected = value != null ? orgs.find(o => o.id === value) : null;

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setQuery(""); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const matches = React.useMemo(() => {
    const term = query.trim().toLowerCase();
    const list = term ? orgs.filter(o => (o.name || "").toLowerCase().includes(term)) : orgs;
    return list.slice(0, ORG_COMBO_LIMIT);
  }, [orgs, query]);

  const choose = (id) => { onChange(id); setOpen(false); setQuery(""); };
  const placeholderText = selected ? selected.name : (allLabel || placeholder || "기관 검색");

  return (
    <div ref={boxRef} style={{ position: "relative", width }}>
      <input
        className="input org-combo-input"
        style={{ width: "100%", color: !open && selected ? "var(--ink-1)" : undefined,
                 borderColor: invalid ? "var(--rose-500)" : undefined }}
        placeholder={placeholderText}
        value={open ? query : (selected ? selected.name : "")}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(""); }}
      />
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
                      background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8,
                      boxShadow: "0 8px 24px rgba(0,0,0,.12)", maxHeight: 280, overflowY: "auto" }}>
          {allLabel && (
            <div onClick={() => choose(null)} className="org-combo-clear"
                 style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13,
                          color: value == null ? "var(--orange-600)" : "var(--ink-1)",
                          background: value == null ? "var(--orange-50)" : "transparent" }}>
              {allLabel}
            </div>
          )}
          {matches.length === 0 && (
            <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--ink-3)" }}>검색 결과 없음</div>
          )}
          {matches.map(o => (
            <div key={o.id} onClick={() => choose(o.id)} className="org-combo-item"
                 style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13,
                          background: o.id === value ? "var(--orange-50)" : "transparent" }}
                 onMouseEnter={e => { if (o.id !== value) e.currentTarget.style.background = "var(--surface-2)"; }}
                 onMouseLeave={e => { e.currentTarget.style.background = o.id === value ? "var(--orange-50)" : "transparent"; }}>
              {o.name}
            </div>
          ))}
          {!query.trim() && orgs.length > matches.length && (
            <div style={{ padding: "6px 12px", fontSize: 11, color: "var(--ink-3)", borderTop: "1px solid var(--border-soft)" }}>
              {orgs.length.toLocaleString()}곳 중 상위 {matches.length}곳 · 이름을 입력해 검색
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export {
  Logo, siteName, brandParts, supportPhone, siteRegion, BRAND_IMG, NCARE, Ic, PasswordInput, PhoneInput, formatPhoneKR, fmtDuration, Sidebar, Topbar, GuardianTabBar, ElderTabBar, RoleTabBar, AvatarImg, AvatarEditor, ProfileEditor, PasswordChanger, AccountWithdraw,
  NotificationPreferences, InlineAlert, LineChart, BarChart, Kpi, useCurrentUser,
  logout, AppFooter, AppFooterCompact, MessageComposer, NotifyFeed, useUnreadCount,
  bpSeverity, hrSeverity, tempSeverity, vitalColorVar, conflictMessage,
  apiErrorMessage, ERROR_CODE_KO, OrgCombobox,
  villageEnabled,
};
